import React from "react";
import Spinner from "@atlaskit/spinner";
import { PeopleCard, PeopleList, Pagination as PaginationUI } from "@caelor/ui";
import {
  Centered,
  PaginationWrapper,
  PeopleCardContainer,
  PeopleListContainer,
  SubtitleContainer,
} from "./styled";
import { useTranslation } from "react-i18next";
import usePeoplePagination from "../../../hooks/use-people-pagination";
import {
  NewPaginationTypesMapper,
  PaginationTypes,
} from "@Shared/Pagination-Types";
import { useRecentlyActiveStore } from "../../../stores/use-recently-active-store";
import { Link } from "../../people/widget/styled";
import { createDynamicProfileLink } from "../../../helpers/cosmos-profile-link";
import { useOpticsStore } from "../../../stores/use-optics-store";

const LinkWrapper = ({ url, mode, openInNewTab, children }) => {
  if (mode === "view") {
    return (
      <Link
        href={url}
        target={openInNewTab ? "_blank" : "_top"}
        rel="noreferrer"
      >
        {children}
      </Link>
    );
  }
  return children;
};

const Mapper = ({ data, children }) => data.map((user) => children(user));

const Variant = (props) => {
  const { variant, children, isLoading } = props;

  if (isLoading)
    return (
      <Centered>
        <Spinner size="xlarge" />
      </Centered>
    );

  if (variant === "card") {
    return (
      <PeopleCardContainer {...props}>
        {children(PeopleCard)}
      </PeopleCardContainer>
    );
  }
  return (
    <PeopleListContainer {...props}>{children(PeopleList)}</PeopleListContainer>
  );
};

const Pagination = ({
  users = [],
  pagination,
  maxNumberOfItems,
  contentFilters = [],
  children,
}) => {
  const { t } = useTranslation();

  const { hasNextPage, itemsPaginated, maxPage, page, setPage } =
    usePeoplePagination({
      pagination,
      items: users,
      maxNumberOfItems,
    });

  const contentFilter = ({ type, status }) =>
    contentFilters.includes(`${type.toLowerCase()}-${status.toLowerCase()}`);

  const filteredItems = itemsPaginated.filter(contentFilter);

  return (
    <PaginationWrapper>
      {children(filteredItems)}
      <PaginationUI
        type={NewPaginationTypesMapper[pagination]}
        labels={{
          next:
            pagination === PaginationTypes.INFINITE
              ? t("show-more")
              : t("next"),
          prev: t("prev"),
        }}
        hasNext={hasNextPage && page < maxPage}
        hasPrev={page > 1}
        onNext={() => hasNextPage && setPage(page + 1)}
        onPrev={() => page > 1 && setPage(page - 1)}
      />
    </PaginationWrapper>
  );
};

const Subtitle = ({ user }) => (
  <SubtitleContainer>
    <span>{user.status} a&nbsp;</span>
    <a href={user.linkToPage} target="_blank" rel="noreferrer">
      {user.type}
    </a>
  </SubtitleContainer>
);

function RecentlyActive({ users, loading, mode }) {
  const { data } = useRecentlyActiveStore((state) => ({
    data: state.data,
  }));

  const isCosmosInstalled = useOpticsStore((state) => state.isCosmosInstalled);

  const {
    pagination,
    maxNumberOfItems,
    view,
    columns,
    borderRadius,
    contentFilters,
    openInNewTab,
  } = data;

  return (
    <Pagination
      users={users}
      pagination={pagination}
      maxNumberOfItems={maxNumberOfItems}
      contentFilters={contentFilters}
    >
      {(paginatedData) => (
        <Variant variant={view} columns={columns} isLoading={loading}>
          {(PeopleComponent) => (
            <Mapper data={paginatedData}>
              {(user) => {
                return (
                  <LinkWrapper
                    key={user.accountId}
                    url={createDynamicProfileLink(
                      user.accountId,
                      isCosmosInstalled
                    )}
                    mode={mode}
                    openInNewTab={openInNewTab ?? true}
                  >
                    <PeopleComponent
                      {...user}
                      key={user.accountId}
                      borderRadius={borderRadius}
                      subtitle={<Subtitle user={user} />}
                      label={user.friendlyModified}
                    />
                  </LinkWrapper>
                );
              }}
            </Mapper>
          )}
        </Variant>
      )}
    </Pagination>
  );
}

export default RecentlyActive;
